import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Anchor,
  Badge,
  Group,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconExternalLink,
  IconInfoTriangle,
  IconLoader2,
  IconX,
} from "@tabler/icons-react";

import { CommissionDetails } from "@components/CommissionDetails";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { ROLES } from "@constants/index";

import { EarningsByService } from "@customTypes/dashboard";
import { Pagination } from "@customTypes/index";


interface PayoutServicesTableProps {
  data: EarningsByService[] | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  pagination: Pagination;
  onPageChange: (page: number) => void;
  userRole: "admin" | "staff";
  columnCount: number;
}

export const PayoutServicesTable = ({
  data,
  isLoading,
  isError,
  isSuccess,
  pagination,
  onPageChange,
  userRole,
  columnCount,
}: PayoutServicesTableProps) => {
  const { t, i18n } = useTranslation();

  const isAdmin = userRole === ROLES.ADMIN;

  const rows = useMemo(() => {
    return data?.map(item => (
      <Table.Tr key={item.paymentId}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {formatDateToString(item.dateOfService)}
            </Text>
            <Text c="dimmed" fz={13}>
              {item.dateOfService.toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          {item.consultationId ? (
            <Anchor
              component={Link}
              to={`/patients/${item.patientId}/consultation/general/${item.consultationId}`}
              ff={"var(--font-secondary)"}
              fz={14}
              c="accent"
              fw={500}>
              {item.cId}
            </Anchor>
          ) : (
            <Text fz={14}>{item.cId}</Text>
          )}
        </Table.Td>
        <Table.Td tt="capitalize">{item.serviceName.replaceAll("_", " ")}</Table.Td>
        <Table.Td>
          <Group gap={5} align="center">
            <Badge
              w={"max-content"}
              fz={12}
              c={item.billStatus === "pending" ? "yellow.8" : "green.8"}
              bg={item.billStatus === "pending" ? "yellow.0" : "green.0"}>
              {item.billStatus}
            </Badge>

            {item.consultationId && (
              <Link
                to={`/patients/${item.patientId}/billing/${item.consultationId}`}
                style={{ lineHeight: 0 }}>
                <IconExternalLink size={16} stroke={1.5} />
              </Link>
            )}
          </Group>
        </Table.Td>
        
        {isAdmin && <Table.Td>{item.staffName}</Table.Td>}

        <Table.Td>
          <Badge
            w={"max-content"}
            fz={12}
            c={item.reportStatus === "incomplete" ? "red.8" : "green.8"}
            bg={item.reportStatus === "incomplete" ? "red.0" : "green.0"}>
            {item.reportStatus}
          </Badge>
        </Table.Td>
        <Table.Td>
          {Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "CFA",
          }).format(item.amountPaidIn || 0)}
        </Table.Td>
        <Table.Td>
          <Group gap={5} align="center">
            {Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "CFA",
            }).format(item.amountDue || 0)}
            <CommissionDetails
              percentage={item.commissionPercentage}
              source={item.commissionPercentageSource}
            />
            {item.payoutStatus === "complete" || item.payoutStatus === "completed" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("complete")}
                withArrow>
                <IconCheck size={16} stroke={1.5} color="green" />
              </Tooltip>
            ) : null}
            {item.payoutStatus === "pending" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("pending")}
                withArrow>
                <IconInfoTriangle size={16} stroke={2} color="orange" />
              </Tooltip>
            ) : null}
            {item.payoutStatus === "processing" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("processing")}
                withArrow>
                <IconLoader2 size={16} stroke={2} color="blue" style={{ animation: "spin 2s linear infinite" }} />
              </Tooltip>
            ) : null}
            {item.payoutStatus === "failed" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("failed")}
                withArrow>
                <IconX size={16} stroke={1.5} color="red" />
              </Tooltip>
            ) : null}
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [data, i18n.language, isAdmin, t]);

  return (
    <>
      <Table
        withRowBorders={true}
        mt={"1rem"}
        miw={1000}
        withTableBorder
        highlightOnHover
        highlightOnHoverColor="var(--clr-gray-100)">
        <Table.Thead>
          <Table.Tr bg={"var(--clr-gray-100)"}>
            <Table.Th>{t("date-of-service")}</Table.Th>
            <Table.Th>{t("consultation-id")}</Table.Th>
            <Table.Th>{t("service")}</Table.Th>
            <Table.Th>{t("bill-status")}</Table.Th>
            {isAdmin && <Table.Th>{t("doctor")}</Table.Th>}
            <Table.Th>{t("report")}</Table.Th>
            <Table.Th>{t("amount-paid-in")}</Table.Th>
            <Table.Th>{t("doctor-earnings")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {renderLoadingErrorData({
            isLoading,
            isError,
            isSuccess,
            dataLength: data?.length ?? 0,
            emptyDataComponent: (
              <TableEmptyRow
                colSpan={columnCount}
                message={t("empty-data")}
              />
            ),
            errorComponent: (
              <TableErrorRow
                colSpan={columnCount}
                message={t("encountered-error")}
              />
            ),
            loadingComponent: (
              <TableSkeletonRows
                columns={columnCount}
                skeletonProps={{
                  widths: Array(columnCount).fill("md"),
                }}
              />
            ),
            dataComponent: rows,
          })}
        </Table.Tbody>
      </Table>
      <TablePagination
        paginationData={pagination}
        paginatedRows={data?.length || 0}
        onPageChange={onPageChange}
        isLoading={isLoading}
      />
    </>
  );
};
